import { useEffect, useState } from "react";

import { H5 } from "./base/TextComponents/H5";
import { Modal } from "./base/Modal";
import { P1 } from "./base/TextComponents/P1";
import { P2B } from "./base/TextComponents/P2B";
import SelectedFile from "./SelectedFile";
import { StandardButton } from "./buttons/StandardButton";
import cloneDeep from "lodash/cloneDeep";
import { useLDClient } from "launchdarkly-react-client-sdk";

// VM-9407 follow-up: "Other Supporting Files" was removed as a selectable upload
// type. This department-targeted flag re-enables it for departments that need it
// (default OFF). Kept as a local string constant because shared-components must
// not depend on the versemedical-only LDFlag enum; the value must match
// LDFlag.OTHER_SUPPORTING_FILES_UPLOAD in featureFlagsUtil.tsx. On the ops side
// the type dropdown is hidden (hideFileTypeSelection), so this read is moot there.
const OTHER_SUPPORTING_FILES_FLAG = "enable-other-supporting-files-upload-type";

interface Props {
  setHideModal: (value: boolean) => void;
  files: File[];
  setFiles: (value: File[]) => void;
  setFilesWithTypes: (value: Record<string, File[]>) => void;
  inputId: string;
  hideFileTypeSelection?: boolean; // If true, hide file type dropdown and auto-assign "Unspecified"
}

export default function FileUploadCategorizationModal(props: Props) {
  const [fileTypes, setFileTypes] = useState<string[]>([]);

  // Resolves against the department context on the customer side; returns false
  // when no LDProvider/context is present.
  const ldClient = useLDClient();
  const showOtherSupportingFiles =
    ldClient?.variation(OTHER_SUPPORTING_FILES_FLAG, false) ?? false;

  useEffect(() => {
    setFileTypes(Array(props.files.length).fill(""));
  }, [props.files]);

  enum fileTypeOptions {
    ProgressNote = "Progress Note",
    PatientDemographics = "Patient Demographics",
    OrderCorrections = "Order Corrections",
    Signature = "Signature",
    OtherSupportingFiles = "Other Supporting Files",
    Unspecified = "Unspecified",
  }

  const updateFileType = (index: number, fileType: string) => {
    const newFileTypes = cloneDeep(fileTypes);
    newFileTypes[index] = fileType;
    setFileTypes(newFileTypes);
  };

  return (
    <Modal
      style="tw-mt-[30vh] tw-bg-gray-light-3 tw-w-[550px] tw-overflow-y-scroll"
      hideModal={() => {
        props.setFiles([]);
        props.setFilesWithTypes({});
        props.setHideModal(true);
      }}
      disableBackdropClose //disable click outside to close
    >
      <div className="tw-bg-gray-light-3 tw-p-[16px]">
        <div className="clearfix">
          <H5 className="tw-float-left">
            {props.hideFileTypeSelection ? "Select Files" : "Select File Types"}
          </H5>
          <div className="tw-float-right">
            <div
              className="tw-h-[36px] tw-cursor-pointer tw-rounded-md tw-border-none tw-bg-transparent tw-px-[8px] tw-py-[10px] hover:tw-bg-blue-light-2"
              onClick={() => {
                window.document.getElementById(props.inputId)?.click();
              }}
            >
              <i className="fas fa-plus tw-mr-[8px] tw-text-blue-1" />
              <P2B className="tw-inline-block !tw-text-blue-1">
                Add more files
              </P2B>
            </div>
          </div>
        </div>
        <div className="clearfix tw-my-[16px] tw-h-fit">
          <div className="clearfix tw-mb-[5px] tw-h-fit">
            <P1 className="tw-float-left tw-text-gray-3">
              {props.files.length} files selected
            </P1>
          </div>
          {props.files.map((file, index) => {
            return (
              <div key={index} className="tw-flex tw-items-center">
                <SelectedFile
                  key={index}
                  file={file}
                  onClick={() => {
                    const newFiles = props.files
                      .slice(0, index)
                      .concat(props.files.slice(index + 1));
                    props.setFiles(newFiles);
                  }}
                  className="tw-w-full tw-border-[1px]"
                />
                {!props.hideFileTypeSelection && (
                  <select
                    className={`tw-mb-[5px] tw-ml-[16px] tw-h-[45px] tw-min-w-[214px] tw-rounded-md tw-border-[1px] tw-border-gray-5 tw-p-[5px] ${
                      fileTypes[index] === ""
                        ? "tw-border-[2px] tw-border-solid tw-border-orange-1"
                        : ""
                    }`}
                    value={fileTypes[index] ?? ""}
                    onChange={(e) => updateFileType(index, e.target.value)}
                  >
                    <option disabled={true} value={""}>
                      (select type)
                    </option>
                    {Object.values(fileTypeOptions)
                      .filter((type) => type !== fileTypeOptions.Unspecified)
                      .filter(
                        (type) =>
                          type !== fileTypeOptions.OtherSupportingFiles ||
                          showOtherSupportingFiles,
                      )
                      .map((type) => {
                        return (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        );
                      })}
                  </select>
                )}
              </div>
            );
          })}
        </div>
        {props.files.reduce((partialSum, file) => partialSum + file.size, 0) /
          1048576 >
        100.0 ? (
          <div className="tw-text-orange-0">
            Total file size cannot exceed 200MB. Please upload smaller files.
          </div>
        ) : null}
      </div>
      <div className="tw-grid tw-grid-cols-2 tw-gap-[16px] tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-gray-5 tw-p-[16px]">
        <StandardButton
          className="tw-col-span-1 tw-mr-[16px] tw-h-[36px] tw-w-full hover:tw-mr-[16px]"
          style="secondary-blue"
          onClick={() => {
            props.setFiles([]);
            props.setFilesWithTypes({});
            props.setHideModal(true);
          }}
        >
          Cancel
        </StandardButton>
        <StandardButton
          className="tw-col-span-1 tw-h-[36px] tw-w-full"
          style="primary-blue"
          onClick={() => {
            const filesWithTypes: Record<string, File[]> = {};
            if (props.hideFileTypeSelection) {
              // Auto-assign "Unspecified" type for all files
              filesWithTypes[fileTypeOptions.Unspecified] = props.files;
            } else {
              // Use selected file types
              props.files.forEach((file, index) => {
                if (fileTypes[index] in filesWithTypes) {
                  filesWithTypes[fileTypes[index]].push(file);
                } else {
                  filesWithTypes[fileTypes[index]] = [file];
                }
              });
            }
            props.setHideModal(true);
            props.setFilesWithTypes(filesWithTypes);
            props.setFiles([]);
          }}
          disabled={(() => {
            const totalSizeMB =
              props.files.reduce((sum, file) => sum + file.size, 0) / 1048576;
            const hasNoFiles = props.files.length === 0;
            const exceedsMaxSize = totalSizeMB > 100.0;
            const hasMissingTypes =
              !props.hideFileTypeSelection &&
              (fileTypes.length === 0 || fileTypes.some((type) => type === ""));

            return hasNoFiles || exceedsMaxSize || hasMissingTypes;
          })()}
        >
          Attach files
        </StandardButton>
      </div>
    </Modal>
  );
}
